<template>
  <div class="print-resume">
    <div class="sidebar-wrapper">
      <div class="profile-container">
        <img
            class="profile"
            :src="toBase('/static/img/profile.jpg')"
            :alt="$profile.firstname + ' ' + $profile.lastname">
        <h1 class="name">{{ $profile.firstname + ' ' + $profile.lastname }}</h1>
        <h3 class="tagline">{{ $profile.role }}</h3>
      </div>

      <div class="contact-container container-block">
        <ul class="list-unstyled contact-list">
          <li class="email">
            <font-awesome-icon prefix :icon="['fas', 'envelope']"/>
            <a :href="'mailto:' + $profile.email">{{ $profile.email }}</a>
          </li>
          <li class="phone" v-if="$profile.phone">
            <font-awesome-icon prefix :icon="['fas', 'phone']"/>
            <a :href="'tel:' + $profile.phone">{{ $profile.phone }}</a>
          </li>
          <li class="website">
            <font-awesome-icon prefix :icon="['fas', 'globe']"/>
            <a
              href="http://www.dcc.fc.up.pt/~arf"
              target="_blank"
            >www.dcc.fc.up.pt/~arf</a>
          </li>
          <li v-if="$profile.social.facebook">
            <font-awesome-icon prefix :icon="['fab', 'facebook-f']"/>
            {{ $profile.social.facebook }}
          </li>
          <li v-if="$profile.social.twitter">
            <font-awesome-icon prefix :icon="['fab', 'twitter']"/>
            {{ $profile.social.twitter }}
          </li>
          <li v-if="$profile.social.linkedin">
            <font-awesome-icon prefix :icon="['fab', 'linkedin-in']"/>
            {{ $profile.social.linkedin }}
          </li>
          <li v-if="$profile.social.github">
            <font-awesome-icon prefix :icon="['fab', 'github']"/>
            {{ $profile.social.github }}
          </li>
          <li v-if="$profile.social.researchgate">
            <icon-researchgate width="14" height="14" class="svg-inline--fa"/>
            {{ $profile.social.researchgate }}
          </li>
          <li v-if="$profile.social.orcid">
            <icon-orcid-id width="14" height="14" class="svg-inline--fa"/>
            {{ $profile.social.orcid }}
          </li>
          <li v-if="$profile.social.googlescholar">
            <icon-googlescholar width="14" height="14" class="svg-inline--fa"/>
            {{ $profile.social.googlescholar }}
          </li>
        </ul>
      </div>

      <div class="education-container container-block">
        <h2 class="container-block-title">Education</h2>
        <div class="item" v-for="study in studies" :key="study.title">
          <h4 class="title">{{ study.title }}</h4>
          <h5 class="meta">{{ study.school }}</h5>
          <div class="time">{{ dateRange(study.start, study.end) }}</div>
        </div>
      </div>
    </div>

    <div class="main-wrapper">
      <section class="section experiences-section">
        <h2 class="section-title">
          <span class="icon-holder">
            <font-awesome-icon prefix :icon="['fas', 'briefcase']"/>
          </span>Experience
        </h2>

        <div class="item" v-for="work in works" :key="work.title">
          <div class="meta">
            <div class="upper-row">
              <h4 class="title">{{ work.title }}</h4>
              <div class="time">{{ dateRange(work.start, work.end) }}</div>
            </div>
            <div class="company">{{ work.company }}</div>
          </div>
        </div>
      </section>

      <section class="section students-section">
        <h2 class="section-title">
          <span class="icon-holder">
            <font-awesome-icon prefix :icon="['fas', 'user-graduate']"/>
          </span>Supervised Works
        </h2>
        <div class="item" v-for="student in students" :key="student.id">
          <h4 class="title">
            {{ student.title }}
          </h4>
          <div class="name">{{ student.student }}</div>
          <div class="institution">{{ student.institution }}</div>
          <div class="time">{{ student.year }}</div>
        </div>
      </section>

      <section class="section projects-section">
        <h2 class="section-title">
          <span class="icon-holder">
            <font-awesome-icon prefix :icon="['fas', 'archive']"/>
          </span>Projects
        </h2>
        <div class="item" v-for="project in projects" :key="project.id">
          <h4 class="title">
            {{ project.title }}
          </h4>
          <div class="description" v-html="project.description"></div>
          <div class="time">{{ dateRange(project.start, project.end) }}</div>
        </div>
      </section>

      <section class="section publications-section">
        <h2 class="section-title">
          <span class="icon-holder">
            <font-awesome-icon prefix :icon="['fas', 'book']"/>
          </span>Publications
        </h2>

        <div class="item" v-for="publication in publications" :key="publication.id">
          <p>{{ authorNames(publication.authors) }}. "{{ publication.title }}". <i>{{ publication.journal }} ({{ publication.date.year }})</i>.
          {{ publication.externalIds.doi ? 'https://dx.doi.org/' + publication.externalIds.doi : '' }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'

import IconResearchGate from '@/icons/typography/IconResearchGate'
import IconOrcidId from '@/icons/typography/IconOrcidId'
import IconGoogleScholar from '@/icons/typography/IconGoogleScholar'

import { generateUUID, authorNames } from '@/utils/utils.js'

export default {
  name: 'print-resume',

  components: {
    'icon-orcid-id': IconOrcidId,
    'icon-researchgate': IconResearchGate,
    'icon-googlescholar': IconGoogleScholar
  },

  props: {
    /* experience: {
      type: Array,
      required: true
    },
    publications: {
      type: Array,
      required: true
    } */
  },

  data () {
    return {}
  },

  computed: {
    ...mapGetters(['toBase']),
    ...mapState(['$profile', '$studies', '$students', '$works', '$projects', '$publications']),

    studies () {
      let res = this.$studies
      return res.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0))
    },

    works () {
      let res = this.$works
      return res.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0))
    },

    students () {
      let res = this.$students.map(student => { student.id = generateUUID(); return student })
      return res.sort((a, b) => (b.year || 0) - (a.year || 0))
    },

    projects () {
      let res = this.$projects
      return res.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0))
    },

    publications () {
      let res = this.$publications
      return res.sort((a, b) => new Date(b.date.year || 0) - new Date(a.date.year || 0))
    }
  },

  mounted () {},

  methods: {
    ...mapMutations(['increase$loading', 'decrease$loading']),

    authorNames,

    dateRange (start, end) {
      if (!start && !end) {
        return ''
      }
      if (!start) {
        return end
      }
      if (!end) {
        return start + ' - Present'
      }
      return start + ' - ' + end
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../node_modules/bootstrap/scss/bootstrap.scss";
@import "@/styles/_variables.scss";

h1,
h2,
h3,
h4,
h5,
h6 {
  font-weight: 700;
}

a {
  color: darken($dark-shades, 15%);
  @include transition(all 0.4s ease-in-out);
  &:hover {
    text-decoration: underline;
    color: darken($dark-shades, 30%);
  }
  &:focus {
    text-decoration: none;
  }
}

p {
  line-height: 1.5;
}

.print-resume {
  --page-width: 8.5in;
  --page-height: 11in;
  --main-width: 6.0in;
  --sidebar-width: calc(var(--page-width) - var(--main-width));

  /* background: $main; */
  width: var(--page-width);
  margin: 0;
  position: relative;
  @include box-shadow(0px 2px 4px rgba(0, 0, 0, 0.1));
}

.item {
  page-break-inside: avoid;
}

.sidebar-wrapper {
  /* background: $main !important; */
  -webkit-print-color-adjust: exact;
  position: absolute;
  right: 0;
  width: var(--sidebar-width);
  height: 100%;
  min-height: 800px;
  color: $dark-shades;
  border-left: 2px solid $light-shades;
  a {
    color: $dark-shades;
  }
  .profile-container {
    padding: 30px;
    //background: darken($main, 10%);
    //background: rgba(0, 0, 0, 0.2);
    text-align: center;
    color: $dark-shades;
  }
  .name {
    font-size: 32px;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 10px;
  }
  .tagline {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
    font-weight: 400;
    margin-top: 0;
    margin-bottom: 0;
  }
  .profile {
    max-width: 100%;
    margin-bottom: 15px;
  }
  .contact-list {
    .svg-inline--fa {
      margin-right: 5px;
      font-size: 14px;
      vertical-align: middle;
      margin-bottom: 0;
    }
    li {
      margin-bottom: 15px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .container-block {
    padding: 30px;
  }
  .container-block-title {
    text-transform: uppercase;
    font-size: 16px;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 15px;
  }
  .education-container {
    .item {
      margin-bottom: 15px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    .title {
      font-weight: 500;
      font-size: 14px;
      margin-top: 0;
      margin-bottom: 5px;
    }
    .meta {
      color: rgba(0, 0, 0, 0.6);
      font-weight: 400;
      margin-bottom: 0px;
      margin-top: 0;
      font-size: 14px;
    }
    .time {
      color: rgba(0, 0, 0, 0.6);
      font-weight: 300;
      margin-bottom: 0px;
    }
  }
}

.main-wrapper {
  /* background: $light-shades; */
  padding: 30px;
  padding-right: calc(var(--sidebar-width) + 30px);
  .section-title {
    text-transform: uppercase;
    font-size: 20px;
    font-weight: 700;
    color: $dark-shades;
    position: relative;
    margin: 20px 0;
    .icon-holder {
      width: 30px;
      height: 30px;
      margin-right: 8px;
      display: inline-block;
      color: $dark-shades;
      @include border-radius(50%);
      text-align: center;
      font-size: 16px;
      position: relative;
      top: -8px;
      .svg-inline--fa {
        font-size: 14px;
        margin-top: 6px;
      }
    }
  }
  .section {
    margin-bottom: 60px;
    page-break-before: auto;
    page-break-after: auto;

    .item {
      position: relative;
      margin-bottom: 30px;
    }
  }

  h4.title {
    font-size: 16px;
    font-weight: 400;
    color: $dark-shades;
    margin-top: 0;
    margin-bottom: 14px;
    padding-right: 100px;
  }

  .experiences-section {
    .item {
    }
  }
  .upper-row {
    position: relative;
    overflow: hidden;
    margin-bottom: 2px;
  }

  .time {
    position: absolute;
    right: 0;
    top: 0;
    color: $dark-shades;
    font-weight: 300;
  }
  .company {
    margin-bottom: 10px;
    color: $dark-shades;
  }
  .projects-section {
    .item {
      margin-bottom: 15px;
      .description {
        p {
          margin-block-start: 0;
        }
      }
      .time {
        font-weight: 300;
        position: absolute;
        right: 0;
        top: 0;
        color: $dark-shades;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/PrintResume.vue